import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const blankLine = () => ({ sku: "", qty: 1 });

export default function ReturnPhysicalIntake({ onChanged }) {
  const [awb, setAwb] = useState("");
  const [orderId, setOrderId] = useState("");
  const [match, setMatch] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [lines, setLines] = useState([blankLine()]);
  const [receivedDate, setReceivedDate] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const locked = !!caseData?.locked;

  const reset = () => {
    setAwb("");
    setOrderId("");
    setMatch(null);
    setCaseData(null);
    setLines([blankLine()]);
    setReceivedDate("");
    setNotes("");
  };

  const searchAwb = async () => {
    const value = awb.trim();
    if (!value) { toast.error("No. Resi retur wajib diisi"); return; }
    setBusy(true);
    try {
      const { data } = await api.get("/returns/physical/match", {
        params: { return_awb: value, source_order_id: orderId.trim() || undefined },
      });
      setMatch(data);
      if (data.existing_case) {
        setCaseData(data.existing_case);
        setOrderId(data.existing_case.source_order_id || "");
        setReceivedDate(data.existing_case.received_date?.slice(0, 10) || "");
        setNotes(data.existing_case.notes || "");
        setLines(data.existing_case.lines.length
          ? data.existing_case.lines.map((x) => ({ id: x.id, sku: x.sku || "", qty: x.qty || 1 }))
          : [blankLine()]);
      } else {
        setCaseData(null);
        if (data.order?.source_order_id) setOrderId(data.order.source_order_id);
        const items = data.order?.items || [];
        setLines(items.length
          ? items.map((x) => ({ sku: x.sku || "", qty: x.qty || 1 }))
          : [blankLine()]);
      }
      toast.success(data.match_status === "MATCHED" ? "Resi/order MATCHED" : "Resi belum MATCHED — isi barang fisik yang kembali");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal mencari resi retur");
    } finally { setBusy(false); }
  };

  const setLine = (idx, patch) => setLines((prev) => prev.map((x, i) => i === idx ? { ...x, ...patch } : x));
  const addLine = () => setLines((prev) => [...prev, blankLine()]);

  const removeLine = async (idx) => {
    const row = lines[idx];
    if (locked) { toast.error("Case sudah masuk inspeksi; gunakan correction"); return; }
    if (!row?.id) {
      setLines((prev) => prev.filter((_, i) => i !== idx));
      return;
    }
    if (!caseData?.id) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/returns/physical/draft/${caseData.id}/lines/${row.id}`);
      setCaseData(data);
      setLines(data.lines.length
        ? data.lines.map((x) => ({ id: x.id, sku: x.sku || "", qty: x.qty || 1 }))
        : [blankLine()]);
      toast.success("Line draft dihapus");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal menghapus line draft");
    } finally { setBusy(false); }
  };

  const saveDraft = async () => {
    const clean = lines
      .map((x) => ({ ...x, sku: (x.sku || "").trim(), qty: Number(x.qty || 0) }))
      .filter((x) => x.sku && x.qty > 0);
    if (!awb.trim()) { toast.error("No. Resi retur wajib diisi"); return; }
    if (clean.length === 0) { toast.error("Minimal 1 SKU + qty wajib diisi"); return; }
    if (locked) { toast.error("Case sudah masuk inspeksi; gunakan correction"); return; }

    setBusy(true);
    try {
      let current = caseData;
      if (!current) {
        const { data } = await api.post("/returns/physical/draft", {
          return_awb: awb.trim(),
          source_order_id: orderId.trim() || null,
          received_date: receivedDate || null,
          notes: notes.trim() || null,
          lines: clean.map(({ sku, qty }) => ({ sku, qty })),
        });
        current = data;
      } else {
        const { data } = await api.patch(`/returns/physical/draft/${current.id}`, {
          source_order_id: orderId.trim(),
          received_date: receivedDate,
          notes,
        });
        current = data;

        for (const row of clean) {
          if (row.id) {
            const { data: updated } = await api.patch(`/returns/physical/draft/${current.id}/lines/${row.id}`, {
              sku: row.sku,
              qty: row.qty,
            });
            current = updated;
          } else {
            const { data: updated } = await api.post(`/returns/physical/draft/${current.id}/lines`, {
              sku: row.sku,
              qty: row.qty,
            });
            current = updated;
          }
        }
      }
      setCaseData(current);
      setLines(current.lines.length
        ? current.lines.map((x) => ({ id: x.id, sku: x.sku || "", qty: x.qty || 1 }))
        : [blankLine()]);
      toast.success("Draft retur fisik tersimpan");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal menyimpan draft retur");
    } finally { setBusy(false); }
  };

  return (
    <section className="panel" data-testid="physical-return-intake">
      <div className="panel-head">
        <div>
          <h2 className="panel-title">Penerimaan Retur Fisik</h2>
          <div className="muted">Cari resi dulu. MATCHED mengambil referensi order; UNMATCHED tetap bisa dicatat berdasarkan isi fisik.</div>
        </div>
        {(match || caseData) && <button className="btn-ghost" onClick={reset}>Retur Baru</button>}
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ minWidth: 260 }}>
          <label className="field-label">No. Resi Retur *</label>
          <input className="field-input" value={awb} onChange={(e) => setAwb(e.target.value)} disabled={!!caseData} />
        </div>
        <div style={{ minWidth: 230 }}>
          <label className="field-label">Order ID (opsional)</label>
          <input className="field-input" value={orderId} onChange={(e) => setOrderId(e.target.value)} disabled={locked} />
        </div>
        <button className="btn-primary" style={{ width: "auto" }} onClick={searchAwb} disabled={busy || !!caseData}>
          {busy ? "Mencari..." : "Cari Resi"}
        </button>
      </div>

      {match && (
        <div style={{ marginTop: 12 }}>
          <span className={`status-pill ${match.match_status === "MATCHED" ? "on" : "off"}`}>
            {match.match_status}
          </span>
          {match.order && <span style={{ marginLeft: 10 }} className="mono">Order: {match.order.source_order_id}</span>}
        </div>
      )}

      {(match || caseData) && (
        <>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16 }}>
            <div>
              <label className="field-label">Tanggal Diterima</label>
              <input className="field-input" type="date" value={receivedDate} onChange={(e) => setReceivedDate(e.target.value)} disabled={locked} />
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
              <label className="field-label">Catatan</label>
              <input className="field-input" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={locked} />
            </div>
          </div>

          <div className="table-scroll" style={{ marginTop: 16 }}>
            <table className="data-table">
              <thead><tr><th>SKU Fisik</th><th style={{ width: 120 }}>Qty</th><th>Status</th><th style={{ width: 100 }}>Aksi</th></tr></thead>
              <tbody>
                {lines.map((row, idx) => (
                  <tr key={row.id || idx}>
                    <td><input className="field-input mono" value={row.sku} onChange={(e) => setLine(idx, { sku: e.target.value })} disabled={locked} placeholder="SKU" /></td>
                    <td><input className="field-input" type="number" min={1} value={row.qty} onChange={(e) => setLine(idx, { qty: e.target.value })} disabled={locked} /></td>
                    <td>{row.id ? <span className="status-pill on">TERSIMPAN</span> : <span className="status-pill off">BARU</span>}</td>
                    <td>{!locked && <button className="btn-mini" onClick={() => removeLine(idx)} disabled={busy}>Hapus</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!locked && <button className="btn-ghost" style={{ marginTop: 10 }} onClick={addLine}>+ Tambah SKU</button>}

          <div style={{ marginTop: 16, display: "flex", gap: 10, alignItems: "center" }}>
            <button className="btn-primary" style={{ width: "auto" }} onClick={saveDraft} disabled={busy || locked}>
              {caseData ? "Simpan Perubahan Draft" : "Simpan Draft Retur"}
            </button>
            {caseData && <span className={`status-pill ${caseData.match_status === "MATCHED" ? "on" : "off"}`}>{caseData.match_status}</span>}
            {locked && <span className="muted">Sudah masuk inspeksi — draft dikunci.</span>}
          </div>
        </>
      )}
    </section>
  );
}
